<template>
  <div>
    <NavBar v-if="navBar" />
    <AuthBySessionExpired />
    <ChangePassword />
    <ChangeGeolocation :user_id="id" :geolocation="geolocation" @save="get" />
    <SecurityQuestions />

    <div class="container">
      <div class="account-header">
        <div class="header-content">
          <div class="block-content-one">
            <v-btn-previous-page />
            <div class="block-title">
              <h1 class="page-title">
                <i class="fas fa-user-circle"></i> Minha Conta
              </h1>
              <p class="page-subtitle mb-0">
                Gerencie suas informações pessoais e configurações
              </p>
            </div>
          </div>
          <div class="actions-section">
            <v-btn
              v-if="permission('read', 'permission')"
              @click="handleToPermissions"
              class="btn btn-permissions"
            >
              <i class="fas fa-shield-alt mr-2"></i> Permissões
            </v-btn>
          </div>
        </div>
      </div>

      <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
        <div class="account-content mb-5">
          <div class="profile-section">
            <div class="profile-card">
              <b-overlay
                :show="loadingImage"
                opacity="0.99"
                class="image-overlay"
              >
                <div class="profile-image-section">
                  <div class="profile-avatar-container">
                    <img
                      :src="
                        photo_url ? photo_url : require('../assets/user.png')
                      "
                      class="profile-avatar"
                      alt="Foto do perfil"
                    />
                    <div class="avatar-overlay">
                      <i class="fas fa-camera"></i>
                    </div>
                  </div>

                  <div class="profile-image-actions">
                    <span
                      class="btn-upload btn-upload-photo"
                      v-if="permission('edit', 'resident_photo')"
                    >
                      <i class="fas fa-camera mr-2"></i>
                      {{ photo_url ? "Alterar Foto" : "Adicionar Foto" }}
                      <input
                        type="file"
                        @change="updateProfilePic($event.target.files[0])"
                        class="file-input"
                        ref="button_img"
                        accept="image/*"
                      />
                    </span>

                    <span
                      v-if="photo_url"
                      class="btn-upload btn-remove-photo"
                      @click="deleteProfilePic"
                      small
                    >
                      <i class="fas fa-trash mr-1"></i>
                      Remover
                    </span>
                  </div>

                  <div class="image-info">
                    <p class="info-text">
                      <i class="fas fa-info-circle mr-1"></i>
                      <span class="mr-1">Máximo:</span>
                      <strong>10MB</strong>
                    </p>
                    <p class="info-text">
                      <span class="mr-1">Formatos:</span>
                      <strong>JPG, PNG</strong>
                    </p>
                  </div>

                  <ErrorsMessages
                    v-if="new_profile_pic_errors.length > 0"
                    :errors="new_profile_pic_errors"
                  />
                </div>
              </b-overlay>
            </div>
          </div>

          <div class="info-section">
            <ErrorsMessages :errors="errors" />

            <div class="info-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-user mr-2"></i>
                  Informações Pessoais
                </h3>
              </div>
              <div class="card-content">
                <div class="info-grid">
                  <div class="info-item full-width">
                    <label class="info-label">Nome Completo</label>
                    <p class="info-value">{{ name }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">CPF</label>
                    <p class="info-value">{{ cpf }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">E-mail</label>
                    <p class="info-value">{{ email }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Status</label>
                    <span class="status-badge status-active">
                      <i class="fas fa-check-circle mr-1"></i>
                      {{ status_type_name }}
                    </span>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Grupo</label>
                    <p class="info-value">{{ user_group_name }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Perfil</label>
                    <p class="info-value">{{ user_profile_name }}</p>
                  </div>

                  <div v-if="has_coren" class="info-item">
                    <label class="info-label">COREN</label>
                    <p class="info-value">
                      {{ coren.uf }} {{ coren.code }} {{ coren.category }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Certificate
              :user_id="id"
              :use_certificate="use_certificate"
              :protocol="protocol"
              :cpf="cpf"
              :user_bry_certificate="user_bry_certificate"
              @save="retrieve()"
            />

            <div class="info-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-clock mr-2"></i>
                  Detalhes da Conta
                </h3>
              </div>
              <div class="card-content">
                <div class="info-grid">
                  <div class="info-item">
                    <label class="info-label">Data de Criação</label>
                    <p class="info-value">
                      <i class="fas fa-calendar-plus mr-1"></i>
                      {{ formatDate(created_at) }}
                    </p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Última Alteração</label>
                    <p class="info-value">
                      <i class="fas fa-edit mr-1"></i>
                      {{ formatDate(updated_at) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="showSyncUpdateOffline" class="warning-card">
              <div class="warning-content">
                <i class="fas fa-exclamation-triangle warning-icon"></i>
                <div class="warning-text">
                  <h4>Sincronização Offline</h4>
                  <p>
                    Configure suas questões de segurança para acesso offline
                    neste dispositivo.
                  </p>
                </div>
              </div>
            </div>

            <div class="actions-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-cogs mr-2"></i>
                  Configurações da Conta
                </h3>
              </div>
              <div class="card-content">
                <div class="actions-grid">
                  <v-btn
                    class="action-btn security-btn"
                    @click="$bvModal.show('security_questions')"
                  >
                    <i class="fas fa-shield-alt mr-2"></i>
                    <div class="btn-text">
                      <span class="btn-title">Questões de Segurança</span>
                      <span class="btn-subtitle">Acesso offline</span>
                    </div>
                  </v-btn>

                  <v-btn
                    class="action-btn password-btn"
                    @click="$bvModal.show('change_password')"
                  >
                    <i class="fas fa-lock mr-2"></i>
                    <div class="btn-text">
                      <span class="btn-title">Alterar Senha</span>
                      <span class="btn-subtitle">Segurança da conta</span>
                    </div>
                  </v-btn>

                  <v-btn
                    v-if="permission('read', 'geolocation')"
                    class="action-btn location-btn"
                    @click="$bvModal.show('change_geolocation')"
                  >
                    <i class="fas fa-map-marker-alt mr-2"></i>
                    <div class="btn-text">
                      <span class="btn-title">Geolocalização</span>
                      <span class="btn-subtitle">Configurar localização</span>
                    </div>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ChangePassword from "../templates/Account/ChangePassword";
import ChangeGeolocation from "../templates/Account/ChangeGeolocation";
import SecurityQuestions from "../templates/Account/SecurityQuestions";
import Certificate from "../templates/User/certificate";

import api from "../services/api";

import toast from "../lib/ToastNotification";

import { formatDate, onlyCodeCoren } from "../lib/format";

import routes from "../routes";

import { userData } from "../lib/functions";

import permission from "../lib/permission";

import { set } from "idb-keyval";

import VBtnPreviousPage from "../templates/Components/VBtnPreviousPage.vue";

export default {
  name: "Account",
  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    ChangePassword,
    ChangeGeolocation,
    SecurityQuestions,
    VBtnPreviousPage,
    Certificate,
  },
  data() {
    return {
      showSyncUpdateOffline: false,
      navBar: true,
      user: {},
      new_profile_pic: null,
      new_profile_pic_errors: [],

      user_bry_certificate: null,

      use_certificate: 0,
      protocol: null,

      rest_home_id: "",
      id: "",
      name: "",
      cpf: "",
      user_profile_name: "",
      user_group_name: "",
      status_type_name: "",
      coren: {},
      has_coren: false,
      email: "",
      geolocation: null,
      created_at: "",
      updated_at: "",
      photo_url: "",
      loading: true,
      loadingImage: false,
      errors: [],
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,
    formatDate,
    onlyCodeCoren,

    async retrieve() {
      let { user } = userData();

      this.id = user.id;
      this.rest_home_id = user.rest_home_id;

      let { question_answer } = routes.currentRoute.query;

      if (this.errors.length == 0) await this.get();
      if (this.errors.length == 0) await this.getOfflineAccess();
      if (this.errors.length > 0) return;

      if (question_answer) {
        setTimeout(() => this.$bvModal.show("security_questions"), 200);
      }

      await this.getUserBryCertificateByUserId();
    },

    async getUserBryCertificateByUserId() {
      try {
        const { id } = this;

        const response = await api.post("/bry/user/certificate", {
          id,
        });

        const { user_bry_certificate } = response.data;

        this.user_bry_certificate = user_bry_certificate;
      } catch (error) {
        console.log(error);
      }
    },

    async getOfflineAccess() {
      try {
        const response = await api.get("/rest-home/users/offline-access");
        const { users } = response.data;

        let {
          user: { email },
        } = this;

        let user = users.find((user) => user.email == email);

        if (!user) {
          this.showSyncUpdateOffline = true;
        } else {
          this.showSyncUpdateOffline = false;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async get() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/user/self");

        const { user, error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.user = user;
          this.name = user.name;
          this.use_certificate = user.use_certificate;
          this.protocol = user.protocol;
          this.email = user.email;
          this.cpf = user.cpf;
          this.user_profile_name = user.user_profile_name;
          this.user_group_name = user.user_group_name;
          this.status_type_name = user.status_type_name;
          this.geolocation = user.geolocation;
          this.created_at = user.created_at;
          this.updated_at = user.updated_at;
          this.photo_url = user.photo_url;

          var coren = onlyCodeCoren(user.professional_class_council_value);

          if (coren) {
            this.coren = coren;
            if (user.professional_class_council_value) {
              this.has_coren = true;
            } else {
              this.has_coren = false;
            }
          }

          this.errors = [];
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async updateProfilePic(image) {
      try {
        if (!image) {
          return;
        }

        this.loadingImage = true;

        let photo = image;
        if (photo.size > 10 * 1024 * 1024) {
          this.new_profile_pic_errors.push({
            id: this.new_profile_pic_errors.length,
            message: "A imagem deve ter até 10MB",
          });
          this.new_profile_pic = null;
          return;
        }

        let formData = new FormData();
        formData.append("photo", photo, photo.name);
        let response = await api.put("/rest-home/user/self/picture", formData);
        let { error, errors, message } = response.data;

        if (error) {
          this.new_profile_pic_errors = errors;
        } else {
          this.new_profile_pic = null;
          this.new_profile_pic_errors = [];
          await this.get();
          this.updateNavBar();
          toast(message, "success");
        }
        this.loadingImage = false;
      } catch (error) {
        console.log(error);
      }
    },

    updateNavBar() {
      sessionStorage.setItem("user", JSON.stringify(this.user));
      this.navBar = false;
      setTimeout(() => (this.navBar = true), 100);
    },

    async deleteProfilePic() {
      try {
        this.loadingImage = true;

        const response = await api.delete("/rest-home/user/self/picture");
        const { error, message, errors } = response.data;

        if (error) {
          this.new_profile_pic_errors = errors;
        } else {
          this.new_profile_pic_errors = [];
          await this.get();
          this.updateNavBar();
          toast(message, "success");
        }

        this.loadingImage = false;
      } catch (error) {
        console.log(error);
      }
    },

    async update() {
      try {
        this.loading = true;

        const { name } = this;

        let params = { name };

        const response = await api.patch("/rest-home/user/update", params);

        const { error, errors, message, user } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          toast(message, "success");
          this.errors = [];
          await set("user", user);
          sessionStorage.setItem("rest_home_id", user.rest_home_id);
          sessionStorage.setItem("rest_home_name", user.rest_home_name);
          sessionStorage.setItem("user", JSON.stringify(user));
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    handleToPermissions() {
      routes.push({
        name: "permissions",
        params: {
          profile_id: this.user_profile_id,
          profile_name: this.name,
        },
      });
    },
  },
};
</script>